// 3_pages/ordering_process_2.js
// Checkout step 2 (body.id--16): disables delivery/payment inputs until a method is
// chosen, keeps the payment block disabled until a delivery method is active,
// shows shipping ETA dates, and resets the recap text per language when nothing is selected.

import { getDeliveryEtaText, loadPublicHolidays } from "../1_utils/delivery_date.js";

let shippingHolidays = new Set();

function insertShippingMethodsHeader(container) {
	const deliveryBox = container.closest(".co-delivery-method");
	if (!deliveryBox) return;

	let header = deliveryBox.querySelector(".shipping-methods-header");
	if (!header) {
		header = document.createElement("div");
		header.className = "shipping-methods-header";
		header.setAttribute("aria-hidden", "true");

		const when = document.createElement("span");
		when.className = "shipping-methods-header__when";
		when.textContent = window.NAT.translations("cartDeliveryWhen");

		const price = document.createElement("span");
		price.className = "shipping-methods-header__price";
		price.textContent = window.NAT.translations("cartPriceWithVat");

		header.append(when, price);
	}

	let row = deliveryBox.querySelector(".shipping-methods-heading-row");
	if (!row) {
		row = document.createElement("div");
		row.className = "shipping-methods-heading-row";
		const heading = deliveryBox.querySelector("h4, h2.h4, h2");
		if (heading) {
			heading.before(row);
			row.append(heading);
		} else {
			container.before(row);
		}
	}

	if (header.parentElement !== row) {
		row.append(header);
	}
}

function applyShippingDeliveryDates(container, holidays) {
	container.querySelectorAll(".radio-wrapper").forEach((row) => {
		const label = row.querySelector("label");
		if (!label) return;
		const code = row.querySelector("input[data-code]")?.getAttribute("data-code") || "";
		const text = getDeliveryEtaText({
			isZasilkovna: isZasilkovnaPickupPoint(row, code),
			isPersonalPickup: code === "personal-collection",
			holidays,
		});
		let eta = label.querySelector(".delivery-eta");
		if (!eta) {
			eta = document.createElement("span");
			eta.className = "delivery-eta";
			const price = label.querySelector(".payment-shipping-price");
			if (price) {
				price.before(eta);
			} else {
				label.append(eta);
			}
		}
		eta.textContent = text;
	});
}

function isZasilkovnaPickupPoint(row, code) {
	if (code !== "zasilkovna" && code !== "logistics-packeta") return false;
	return Boolean(row.querySelector("input.zasilkovna-choose-branch, input.choose-branch, [data-testid='deliveryPoint']"));
}

function refreshShippingDeliveryDates() {
	const container = document.querySelector("#order-shipping-methods");
	if (!container) return;
	insertShippingMethodsHeader(container);
	applyShippingDeliveryDates(container, shippingHolidays);
	loadPublicHolidays().then((holidays) => {
		shippingHolidays = holidays;
		applyShippingDeliveryDates(container, holidays);
	});
}

const CHECKOUT_SHIPPING_STORAGE_KEY = "natCheckoutShippingMethod";
const CHECKOUT_PAYMENT_STORAGE_KEY = "natCheckoutPaymentMethod";

function disableInputs(method) {
	if (!method) return;
	const allInputs = method.querySelectorAll("input");
	if (!allInputs) return;
	allInputs.forEach((input) => {
		input.checked = false;
		input.parentElement.classList.remove("active");
	});
}

function removeDeliveryFromRecap() {
	const recapText = document.querySelector(".recapitulation-shipping-billing .recapitulation-shipping-billing-info");
	if (recapText) {
		recapText.textContent = window.NAT.translations("chooseDeliveryMethod");
	}
}

function removePaymentFromRecap() {
	const recapText = document.querySelector(
		".recapitulation-shipping-billing.last .recapitulation-shipping-billing-info",
	);
	if (recapText) {
		recapText.textContent = window.NAT.translations("choosePaymentMethod");
	}
}

function getActiveMethodRadio(containerSelector) {
	return (
		document.querySelector(`${containerSelector} > .radio-wrapper.active input[type="radio"]`) ||
		document.querySelector(`${containerSelector} input[type="radio"]:checked`)
	);
}

function saveCheckoutMethod(storageKey, containerSelector) {
	const input = getActiveMethodRadio(containerSelector);
	if (!input) return;

	const row = input.closest(".radio-wrapper");
	const branch = row?.querySelector(
		"input.zasilkovna-choose-branch, input.choose-branch, input[data-testid='deliveryPoint']",
	);

	const payload = {
		id: input.id || "",
		value: input.value || "",
		branchValue: branch?.value || "",
	};

	try {
		sessionStorage.setItem(storageKey, JSON.stringify(payload));
	} catch (error) {
		console.warn("Could not save checkout method selection:", error);
	}
}

function clearCheckoutMethod(storageKey) {
	try {
		sessionStorage.removeItem(storageKey);
	} catch (error) {
		console.warn("Could not clear checkout method selection:", error);
	}
}

function readCheckoutMethod(storageKey) {
	try {
		const raw = sessionStorage.getItem(storageKey);
		if (!raw) return null;
		return JSON.parse(raw);
	} catch (error) {
		console.warn("Could not read checkout method selection:", error);
		return null;
	}
}

function restoreCheckoutMethod(storageKey, containerSelector, { quiet = false } = {}) {
	const payload = readCheckoutMethod(storageKey);
	if (!payload) return false;

	const container = document.querySelector(containerSelector);
	if (!container) return false;

	let input = payload.id ? document.getElementById(payload.id) : null;
	if (!input && payload.value) {
		input = container.querySelector(`input[type="radio"][value="${CSS.escape(payload.value)}"]`);
	}
	if (!input || !container.contains(input)) return false;

	const row = input.closest(".radio-wrapper");
	if (payload.branchValue) {
		const branch = row?.querySelector(
			"input.zasilkovna-choose-branch, input.choose-branch, input[data-testid='deliveryPoint']",
		);
		if (branch) {
			branch.value = payload.branchValue;
		}
	}

	input.checked = true;
	row?.classList.add("active");
	if (!quiet) {
		input.dispatchEvent(new Event("change", { bubbles: true }));
	}
	return true;
}

let ignorePaymentAutoClearUntil = 0;

function clearPaymentSelection(paymentMethodWrapper) {
	clearCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY);
	removePaymentFromRecap();
	if (paymentMethodWrapper) {
		disableInputs(paymentMethodWrapper);
	}
}

function reassertStoredShippingQuietly() {
	const payload = readCheckoutMethod(CHECKOUT_SHIPPING_STORAGE_KEY);
	if (!payload) return;

	const container = document.querySelector("#order-shipping-methods");
	if (!container) return;

	let input = payload.id ? document.getElementById(payload.id) : null;
	if (!input && payload.value) {
		input = container.querySelector(`input[type="radio"][value="${CSS.escape(payload.value)}"]`);
	}
	if (!input || !container.contains(input)) return;

	const row = input.closest(".radio-wrapper");
	if (payload.branchValue) {
		const branch = row?.querySelector(
			"input.zasilkovna-choose-branch, input.choose-branch, input[data-testid='deliveryPoint']",
		);
		if (branch) {
			branch.value = payload.branchValue;
		}
	}

	input.checked = true;
	row?.classList.add("active");
	document.querySelector(".co-delivery-method")?.classList.add("selected");
}

function ensureNoDefaultPayment(paymentMethodWrapper) {
	if (Date.now() < ignorePaymentAutoClearUntil) return;
	if (readCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY)) return;
	clearPaymentSelection(paymentMethodWrapper);
	paymentMethodWrapper?.classList.remove("disabled");
	reassertStoredShippingQuietly();
}

function restoreCheckoutShippingAndPayment(deliveryMethodWrapper, paymentMethodWrapper) {
	const restoredShipping = restoreCheckoutMethod(CHECKOUT_SHIPPING_STORAGE_KEY, "#order-shipping-methods");
	if (!restoredShipping) return;

	if (deliveryMethodWrapper) {
		deliveryMethodWrapper.classList.add("selected");
	}
	if (paymentMethodWrapper) {
		paymentMethodWrapper.classList.remove("disabled");
	}

	const hasStoredPayment = Boolean(readCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY));

	const applyPaymentAfterShipping = () => {
		if (hasStoredPayment) {
			restoreCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY, "#order-billing-methods");
			return;
		}
		ensureNoDefaultPayment(paymentMethodWrapper);
	};
	setTimeout(applyPaymentAfterShipping, 300);
	setTimeout(applyPaymentAfterShipping, 600);
}

function syncCheckoutShippingUi(deliveryMethodWrapper, paymentMethodWrapper) {
	const activeDeliveryMethod =
		document.querySelector("#order-shipping-methods > .radio-wrapper.active") ||
		getActiveMethodRadio("#order-shipping-methods")?.closest(".radio-wrapper");

	if (activeDeliveryMethod) {
		const previousShipping = readCheckoutMethod(CHECKOUT_SHIPPING_STORAGE_KEY);
		const currentShipping = getActiveMethodRadio("#order-shipping-methods");
		const shippingChanged = previousShipping && currentShipping && previousShipping.value !== currentShipping.value;
		const shippingFirstSelect = !previousShipping && Boolean(currentShipping);
		saveCheckoutMethod(CHECKOUT_SHIPPING_STORAGE_KEY, "#order-shipping-methods");
		if (shippingChanged || shippingFirstSelect) {
			clearCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY);
			ignorePaymentAutoClearUntil = 0;
			setTimeout(() => {
				ensureNoDefaultPayment(paymentMethodWrapper);
			}, 200);
		}
		paymentMethodWrapper?.classList.remove("disabled");
		deliveryMethodWrapper?.classList.add("selected");
	} else {
		deliveryMethodWrapper?.classList.remove("selected");
		clearCheckoutMethod(CHECKOUT_SHIPPING_STORAGE_KEY);
	}
}

function syncCheckoutPaymentUi(deliveryMethodWrapper, paymentMethodWrapper, { userInitiated = false } = {}) {
	const activeDeliveryMethod =
		document.querySelector("#order-shipping-methods > .radio-wrapper.active") ||
		getActiveMethodRadio("#order-shipping-methods")?.closest(".radio-wrapper");
	const activePaymentMethod =
		document.querySelector("#order-billing-methods > .radio-wrapper.active") ||
		getActiveMethodRadio("#order-billing-methods")?.closest(".radio-wrapper");

	if (userInitiated && activePaymentMethod) {
		saveCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY, "#order-billing-methods");
		ignorePaymentAutoClearUntil = Date.now() + 1200;
		if (activeDeliveryMethod) {
			paymentMethodWrapper?.classList.remove("disabled");
			deliveryMethodWrapper?.classList.add("selected");
		}
		return;
	}

	const storedPayment = readCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY);
	const inPaymentLock = Date.now() < ignorePaymentAutoClearUntil;

	if (activePaymentMethod) {
		if (!storedPayment) {
			if (inPaymentLock) {
				saveCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY, "#order-billing-methods");
			} else {
				setTimeout(() => {
					ensureNoDefaultPayment(paymentMethodWrapper);
				}, 0);
			}
		}
	} else if (storedPayment) {
		setTimeout(() => {
			const restored = restoreCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY, "#order-billing-methods", {
				quiet: true,
			});
			if (!restored && Date.now() >= ignorePaymentAutoClearUntil) {
				clearCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY);
				removePaymentFromRecap();
				if (paymentMethodWrapper) {
					disableInputs(paymentMethodWrapper);
				}
			}
			reassertStoredShippingQuietly();
		}, 200);
		if (inPaymentLock) {
			setTimeout(() => {
				restoreCheckoutMethod(CHECKOUT_PAYMENT_STORAGE_KEY, "#order-billing-methods", { quiet: true });
				reassertStoredShippingQuietly();
			}, 500);
		}
	} else {
		setTimeout(() => {
			removePaymentFromRecap();
			if (paymentMethodWrapper) {
				disableInputs(paymentMethodWrapper);
			}
			reassertStoredShippingQuietly();
		}, 200);
	}

	if (activeDeliveryMethod) {
		paymentMethodWrapper?.classList.remove("disabled");
		deliveryMethodWrapper?.classList.add("selected");
	} else {
		deliveryMethodWrapper?.classList.remove("selected");
	}
}

function bindCheckoutMethodPersistence(deliveryMethodWrapper, paymentMethodWrapper) {
	document.addEventListener("ShoptetShippingMethodUpdated", () => {
		syncCheckoutShippingUi(deliveryMethodWrapper, paymentMethodWrapper);
	});
	document.addEventListener("ShoptetBillingMethodUpdated", () => {
		syncCheckoutPaymentUi(deliveryMethodWrapper, paymentMethodWrapper, { userInitiated: false });
	});

	document.querySelector("#order-shipping-methods")?.addEventListener("change", (event) => {
		if (!(event.target instanceof HTMLInputElement) || event.target.type !== "radio") return;
		syncCheckoutShippingUi(deliveryMethodWrapper, paymentMethodWrapper);
	});

	const billingMethods = document.querySelector("#order-billing-methods");
	billingMethods?.addEventListener(
		"pointerdown",
		(event) => {
			if (!(event.target instanceof Element)) return;
			if (!event.target.closest(".radio-wrapper, label, input[type='radio']")) return;
			ignorePaymentAutoClearUntil = Date.now() + 1200;
		},
		true,
	);
	billingMethods?.addEventListener("change", (event) => {
		if (!(event.target instanceof HTMLInputElement) || event.target.type !== "radio") return;
		syncCheckoutPaymentUi(deliveryMethodWrapper, paymentMethodWrapper, { userInitiated: event.isTrusted });
	});
}

export function init() {
	if (!document.body.classList.contains("id--16")) return;

	const deliveryMethodWrapper = document.querySelector(".co-delivery-method");
	const paymentMethodWrapper = document.querySelector(".co-payment-method");

	refreshShippingDeliveryDates();
	document.addEventListener("ShoptetDOMContentLoaded", refreshShippingDeliveryDates);
	document.addEventListener("ShoptetShippingMethodUpdated", refreshShippingDeliveryDates);

	if (deliveryMethodWrapper) {
		disableInputs(deliveryMethodWrapper);
	}
	if (paymentMethodWrapper) {
		paymentMethodWrapper.classList.add("disabled");
		disableInputs(paymentMethodWrapper);
	}

	document.addEventListener("DOMContentLoaded", function () {
		// Always clear Shoptet defaults so nothing is preselected on a fresh visit.
		// Then restore only methods the user already chose in this tab (sessionStorage),
		// so a failed "Continue" (missing payment / pickup) does not wipe shipping again.
		if (deliveryMethodWrapper) {
			disableInputs(deliveryMethodWrapper);
			removeDeliveryFromRecap();
		}
		if (paymentMethodWrapper) {
			paymentMethodWrapper.classList.add("disabled");
			disableInputs(paymentMethodWrapper);
			removePaymentFromRecap();
		}
		refreshShippingDeliveryDates();
		bindCheckoutMethodPersistence(deliveryMethodWrapper, paymentMethodWrapper);
		restoreCheckoutShippingAndPayment(deliveryMethodWrapper, paymentMethodWrapper);
	});
}

/* function disableInputs(method) {
	if (!method) return;
	let allInputs = method.querySelectorAll("input");

	if (!allInputs) return;
	allInputs.forEach((input) => {
		input.checked = false;
		input.parentElement.classList.remove("active");
	});
}

function removeDeliveryFromRecap() {
	let recapText = document.querySelector(".recapitulation-shipping-billing .recapitulation-shipping-billing-info");
	if (recapText) {
		if (csLang) {
			recapText.textContent = "Zvolte způsob dopravy";
		}
		if (skLang) {
			recapText.textContent = "Zvoľte spôsob dopravy";
		}
		if (plLang) {
			recapText.textContent = "Wybierz sposób dostawy";
		}
	}
}
function removePaymentFromRecap() {
	let recapText = document.querySelector(".recapitulation-shipping-billing.last .recapitulation-shipping-billing-info");
	if (recapText) {
		if (csLang) {
			recapText.textContent = "Zvolte způsob platby";
		}
		if (skLang) {
			recapText.textContent = "Zvoľte spôsob platby";
		}
		if (plLang) {
			recapText.textContent = "Wybierz sposób płatności";
		}
	}
} */

/**/
/*
document.addEventListener("DOMContentLoaded", function () {
		if (deliveryMethodWrapper) {
			disableInputs(deliveryMethodWrapper);
			removeDeliveryFromRecap();
		}
		if (paymentMethodWrapper) {
			paymentMethodWrapper.classList.add("disabled");
			disableInputs(paymentMethodWrapper);
			removePaymentFromRecap();
		}
		document.addEventListener("ShoptetShippingMethodUpdated", function () {
			let activeDeliveryMethod = document.querySelector("#order-shipping-methods > .radio-wrapper.active");
			let activePaymentMethod = document.querySelector("#order-billing-methods > .radio-wrapper.active");

			if (!activePaymentMethod) {
				setTimeout(() => {
					removePaymentFromRecap();
					disableInputs(paymentMethodWrapper);
				}, 200);
			}

			if (activeDeliveryMethod) {
				paymentMethodWrapper.classList.remove("disabled");
				deliveryMethodWrapper.classList.add("selected");
			} else {
				deliveryMethodWrapper.classList.remove("selected");
			}
		});
		document.addEventListener("ShoptetBillingMethodUpdated", function () {
			let activeDeliveryMethod = document.querySelector("#order-shipping-methods > .radio-wrapper.active");
			let activePaymentMethod = document.querySelector("#order-billing-methods > .radio-wrapper.active");

			if (!activePaymentMethod) {
				setTimeout(() => {
					removePaymentFromRecap();
					disableInputs(paymentMethodWrapper);
				}, 200);
			}

			if (activeDeliveryMethod) {
				paymentMethodWrapper.classList.remove("disabled");
				deliveryMethodWrapper.classList.add("selected");
			} else {
				deliveryMethodWrapper.classList.remove("selected");
			}
		});
	});

	function disableInputs(method) {
		let allInputs = method.querySelectorAll("input");

		if (!allInputs) return;
		allInputs.forEach((input) => {
			input.checked = false;
			input.parentElement.classList.remove("active");
		});
	}
	function removeDeliveryFromRecap() {
		let recapText = document.querySelector(".recapitulation-shipping-billing .recapitulation-shipping-billing-info");
		if (recapText) {
			if (csLang) {
				recapText.textContent = "Zvolte způsob dopravy";
			}
			if (skLang) {
				recapText.textContent = "Zvoľte spôsob dopravy";
			}
			if (plLang) {
				recapText.textContent = "Wybierz sposób dostawy";
			}
		}
	}
	function removePaymentFromRecap() {
		let recapText = document.querySelector(
			".recapitulation-shipping-billing.last .recapitulation-shipping-billing-info"
		);
		if (recapText) {
			if (csLang) {
				recapText.textContent = "Zvolte způsob platby";
			}
			if (skLang) {
				recapText.textContent = "Zvoľte spôsob platby";
			}
			if (plLang) {
				recapText.textContent = "Wybierz sposób płatności";
			}
		}
	}
*/
